// Turns a tool call's arguments into human-readable rows for the approval card.
// Rows: { label, value, muted, indent }. Nested JSON strings (e.g. the `fields`
// and `commitData` tool parameters) are parsed and flattened; ID-style keys are
// demoted to the bottom and muted so the meaningful data reads first.

const MAX_ROWS = 30
const MAX_DEPTH = 3
const MAX_ARRAY_ITEMS = 8
const MAX_VALUE_CHARS = 200

// Object keys that identify a field-value pair; collapsed to one "name: value" row.
const FIELD_NAME_KEYS = ['fieldVersionReference', 'versionReference', 'code', 'name', 'title', 'key']
// Keys whose values are display-worthy when summarizing an object in one line.
const DISPLAY_KEYS = ['title', 'name', 'code', 'value', 'content']
// Argument keys that carry no meaning for a human approver (drawing-pin coordinates).
const HIDDEN_KEYS = new Set(['x', 'y'])

// Active title map for the current buildApprovalRows call (safe: synchronous, single-threaded).
let titles = {}

// fieldTitles: optional versionReference → human title map (from the backend's
// approval event); when a field reference resolves, its title becomes the row label.
export function buildApprovalRows(args, fieldTitles = null) {
  titles = fieldTitles || {}
  let parsed = deepParse(args)
  if (parsed === null || parsed === undefined) return []
  if (!isObject(parsed)) return [{ label: '', value: formatScalar(parsed), muted: false, indent: 0 }]

  // A lone wrapper param (e.g. commitData) adds no information — hoist its contents.
  const entries = Object.entries(parsed)
  if (entries.length === 1 && isObject(deepParse(entries[0][1]))) {
    parsed = deepParse(entries[0][1])
  }

  const rows = []
  const idRows = []
  for (const [key, raw] of Object.entries(parsed)) {
    if (HIDDEN_KEYS.has(key)) continue
    const value = deepParse(raw)
    if (isEmpty(value)) continue
    // An id that resolves to a title reads as real data ("Location: North Car
    // Park") — keep it with the main rows; only unresolved ids get demoted.
    const unresolvedId = isIdKey(key) && !isObject(value) && !Array.isArray(value) && !titles[String(value)]
    appendRows(unresolvedId ? idRows : rows, key, value, 0)
  }
  const all = [...rows, ...idRows.map((r) => ({ ...r, muted: true }))]
  if (all.length > MAX_ROWS) {
    return [...all.slice(0, MAX_ROWS), { label: '', value: `…and ${all.length - MAX_ROWS} more`, muted: true, indent: 0 }]
  }
  return all
}

function appendRows(rows, key, value, depth) {
  const label = humanizeKey(key)
  if (Array.isArray(value)) {
    if (value.length === 0) return
    if (value.every((v) => !isObject(v) && !Array.isArray(v))) {
      const resolved = value.map((v) => titles[String(v)])
      const items = value.map((v, i) => resolved[i] || formatScalar(v))
      rows.push({
        label: resolved.every(Boolean) ? stripIdSuffix(label) : label,
        value: truncate(items.join(', ')),
        muted: false,
        indent: depth,
      })
      return
    }
    rows.push({ label: `${label} (${value.length})`, value: '', muted: false, indent: depth })
    const shown = value.slice(0, MAX_ARRAY_ITEMS)
    for (const item of shown) appendItemRows(rows, item, depth + 1)
    if (value.length > shown.length) {
      rows.push({ label: '', value: `…and ${value.length - shown.length} more`, muted: true, indent: depth + 1 })
    }
    return
  }
  if (isObject(value)) {
    const collapsed = collapseFieldObject(value)
    if (collapsed) {
      rows.push({ label: collapsed.label, value: collapsed.value, muted: false, indent: depth })
      return
    }
    rows.push({ label, value: '', muted: false, indent: depth })
    appendObjectRows(rows, value, depth + 1)
    return
  }
  if (isIdKey(key) && titles[String(value)]) {
    rows.push({ label: stripIdSuffix(label), value: titles[String(value)], muted: false, indent: depth })
    return
  }
  rows.push({ label, value: formatScalar(value), muted: isIdKey(key), indent: depth })
}

// "Location ID" → "Location", "Viewable location IDs" → "Viewable locations".
function stripIdSuffix(label) {
  if (/ IDs$/.test(label)) return `${label.slice(0, -4)}s`
  return label.replace(/ ID$/, '')
}

function appendObjectRows(rows, obj, depth) {
  if (depth > MAX_DEPTH) {
    rows.push({ label: '', value: '…', muted: true, indent: depth })
    return
  }
  for (const [key, raw] of Object.entries(obj)) {
    const value = deepParse(raw)
    if (isEmpty(value)) continue
    appendRows(rows, key, value, depth)
  }
}

// One row per array item: a field-value pair collapses to "name: value"; other
// objects get a single summary line from their display keys, with nested
// children/subtasks recursed so task trees stay legible.
function appendItemRows(rows, item, depth) {
  const value = deepParse(item)
  if (!isObject(value)) {
    rows.push({ label: '', value: formatScalar(value), muted: false, indent: depth })
    return
  }
  const collapsed = collapseFieldObject(value)
  if (collapsed) {
    rows.push({ label: collapsed.label, value: collapsed.value, muted: false, indent: depth })
    return
  }
  rows.push({ label: '', value: summarizeObject(value), muted: false, indent: depth })
  if (depth >= MAX_DEPTH) return
  for (const childKey of ['children', 'subtasks', 'taskProfiles', 'subtaskProfiles']) {
    const children = deepParse(value[childKey])
    if (Array.isArray(children) && children.length > 0) {
      rows.push({ label: `${humanizeKey(childKey)} (${children.length})`, value: '', muted: false, indent: depth + 1 })
      const shown = children.slice(0, MAX_ARRAY_ITEMS)
      for (const child of shown) appendItemRows(rows, child, depth + 2)
      if (children.length > shown.length) {
        rows.push({ label: '', value: `…and ${children.length - shown.length} more`, muted: true, indent: depth + 2 })
      }
    }
  }
}

// {fieldVersionReference|code|…, value, meta?} → { label: name, value }
function collapseFieldObject(obj) {
  const keys = Object.keys(obj)
  const nameKey = FIELD_NAME_KEYS.find((k) => typeof obj[k] === 'string' && obj[k])
  if (!nameKey) return null
  const rest = keys.filter((k) => k !== nameKey && !isEmpty(obj[k]))
  const hasValue = rest.includes('value') || rest.includes('meta')
  const extras = rest.filter((k) => k !== 'value' && k !== 'meta')
  if (!hasValue || extras.length > 0) return null
  // TeamList/UserList field values are team/user ids — map them through titles
  // (both the direct `value` form and the meta:{teamId|userId} form).
  const rawValue = deepParse(obj.value)
  let display = typeof rawValue === 'string' && titles[rawValue]
    ? titles[rawValue]
    : formatScalar(rawValue)
  if ((display === '' || display === null) && obj.meta) {
    const meta = deepParse(obj.meta)
    display = isObject(meta)
      ? Object.values(meta).map((v) => titles[String(v)] || formatScalar(v)).join(', ')
      : formatScalar(meta)
  }
  return { label: titles[obj[nameKey]] || obj[nameKey], value: truncate(display ?? '') }
}

function summarizeObject(obj) {
  const parts = []
  for (const key of DISPLAY_KEYS) {
    if (!isEmpty(obj[key]) && !isObject(obj[key]) && !Array.isArray(obj[key])) {
      parts.push(formatScalar(obj[key]))
      if (parts.length === 2) break
    }
  }
  if (parts.length === 0) {
    for (const [key, raw] of Object.entries(obj)) {
      if (isIdKey(key) || isObject(raw) || Array.isArray(raw) || isEmpty(raw)) continue
      parts.push(`${humanizeKey(key)}: ${formatScalar(raw)}`)
      if (parts.length === 2) break
    }
  }
  const dates = [obj.startDate, obj.endDate].filter((d) => !isEmpty(d)).map(formatScalar)
  if (dates.length > 0) parts.push(dates.join(' – '))
  return truncate(parts.join(' — ')) || '(item)'
}

function deepParse(value) {
  if (typeof value !== 'string') return value
  const s = value.trim()
  if (!(s.startsWith('{') || s.startsWith('['))) return value
  try {
    return deepParseStructure(JSON.parse(s))
  } catch {
    return value
  }
}

function deepParseStructure(value) {
  if (Array.isArray(value)) return value.map(deepParse)
  if (isObject(value)) {
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = deepParse(v)
    return out
  }
  return value
}

function humanizeKey(key) {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
  const label = words
    .map((w) => (/^ids?$/i.test(w) ? w.toUpperCase() : w.toLowerCase()))
    .join(' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function isIdKey(key) {
  return /^id$/i.test(key) || /Ids?$/.test(key) || /_ids?$/i.test(key)
}

function formatScalar(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') {
    // "image:N" is the tool convention for attaching the Nth conversation
    // image — show what it means, not the token.
    if (/^image:\d+$/i.test(value)) return 'Attached photo'
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/)
    if (m) {
      const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`)
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      }
    }
    return truncate(value)
  }
  return truncate(String(value))
}

function truncate(text) {
  if (typeof text !== 'string') return text
  return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text
}

function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isEmpty(value) {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  if (isObject(value) && Object.keys(value).length === 0) return true
  return false
}
